import { api } from './api';

export const calendarCommunities = {
  GENERAL: 'Geral',
  DEVCLUB: 'DevClub',
  IACLUB: 'IAClub',
  POS_MBA_IA: 'Works AI · MBA',
  MENTORIA: 'Mentoria',
} as const;
export type CalendarCommunity = keyof typeof calendarCommunities;
export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  community: CalendarCommunity;
  eventType: string;
  eventDate: string | null;
  startTime: string | null;
  endTime: string | null;
  weekday: number | null;
  timezone: string;
  meetingUrl: string | null;
  location: string | null;
  coverImage: string | null;
  isActive: boolean;
  isRecurring: boolean;
}
export interface CalendarResult {
  source: 'home-new';
  events: CalendarEvent[];
}
export interface CalendarSettings {
  source: 'home-new';
  configured: boolean;
  communityMemberAreaIds: Partial<Record<CalendarCommunity, string[]>>;
  usesLegacyModuleAccess: boolean;
  unmappedLegacyModulesCount: number;
}
export type CalendarInput = Omit<CalendarEvent, 'id'>;
const unpack = <T>(value: T | { data: T }): T =>
  value && typeof value === 'object' && 'data' in value ? value.data : value;

export const agendaService = {
  async events(community?: CalendarCommunity): Promise<CalendarResult> {
    const { data } = await api.get<CalendarResult | { data: CalendarResult }>(
      '/calendar/events',
      { params: { community } },
    );
    return unpack(data);
  },
  async adminEvents(): Promise<CalendarResult> {
    const { data } = await api.get<CalendarResult | { data: CalendarResult }>(
      '/calendar/admin/events',
    );
    return unpack(data);
  },
  async settings(): Promise<CalendarSettings> {
    const { data } = await api.get<
      CalendarSettings | { data: CalendarSettings }
    >('/calendar/admin/settings');
    return unpack(data);
  },
  async saveSettings(
    communityMemberAreaIds: CalendarSettings['communityMemberAreaIds'],
  ): Promise<CalendarSettings> {
    const { data } = await api.put<
      CalendarSettings | { data: CalendarSettings }
    >('/calendar/admin/settings', { communityMemberAreaIds });
    return unpack(data);
  },
  async saveEvent(value: CalendarInput, id?: string) {
    if (id) await api.put(`/calendar/admin/events/${id}`, value);
    else await api.post('/calendar/admin/events', value);
  },
  async removeEvent(id: string) {
    await api.delete(`/calendar/admin/events/${id}`);
  },
};

export function calendarDateKey(event: CalendarEvent): string | null {
  return event.eventDate?.slice(0, 10) ?? null;
}
export function weeklyEvent(event: CalendarEvent): boolean {
  return (
    event.isRecurring &&
    event.eventDate === null &&
    Number.isInteger(event.weekday) &&
    Number(event.weekday) >= 1 &&
    Number(event.weekday) <= 7
  );
}
export function eventSchedule(event: CalendarEvent): string {
  const weekdays = [
    '',
    'Segunda-feira',
    'Terça-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sábado',
    'Domingo',
  ];
  const date = weeklyEvent(event)
    ? `Toda semana · ${weekdays[Number(event.weekday)]}`
    : event.eventDate
      ? new Intl.DateTimeFormat('pt-BR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(event.eventDate))
      : 'Data a definir';
  const time = event.startTime
    ? `${event.startTime}${event.endTime ? ` – ${event.endTime}` : ''}`
    : 'Horário a definir';
  return `${date} · ${time} (${event.timezone})`;
}
